import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { SelectFormField } from '@/components/common/FormField';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { dateToIsoKey, isoKeyYear } from '@/utils/dateUtils';
import { type LocationModel, locationModelService } from '../services/locationModelService';
import { locationService } from '../services/locationService';
import { type Holiday, HolidayDrawer } from './HolidayDrawer';
import { HolidayYearCalendar } from './HolidayYearCalendar';

type HolidayPanelMode = 'empty' | 'selected' | 'edit' | 'add';

export const HolidayManagement: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const queryClient = useQueryClient();

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationService.getLocations(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const locations = locationsData || [];

  const {
    data: locationModelsData,
    isLoading: isLoadingLocationModels,
    error: locationModelsError,
  } = useQuery({
    queryKey: ['locationModels', selectedOfficeId],
    queryFn: async () => {
      if (!selectedOfficeId) {
        return { success: true, data: [], message: '' };
      }
      try {
        const response = await locationModelService.getLocationModels(selectedOfficeId);
        if (!response) {
          return { success: true, data: [], message: '' };
        }
        const dataArray = Array.isArray(response) ? response : [];
        return { success: true, data: dataArray, message: '' };
      } catch (error) {
        return {
          success: false,
          data: [],
          message: error instanceof Error ? error.message : 'Failed to fetch location models',
        };
      }
    },
    enabled: !!selectedOfficeId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const locationModels = Array.isArray(locationModelsData?.data) ? locationModelsData.data : [];

  const holidays: Holiday[] = useMemo(() => {
    return locationModels.map((lm: LocationModel) => ({
      id: lm.id,
      name: lm.name || `Holiday ${lm.date_of_year}`,
      date: lm.date_of_year,
      type: 'national' as const,
      isRecurring: false,
      description: `Not working: ${(lm.not_working_percentage * 100).toFixed(1)}%`,
      officeId: lm.location_id,
      not_working_percentage: lm.not_working_percentage,
    }));
  }, [locationModels]);

  const [holidayPanelMode, setHolidayPanelMode] = useState<HolidayPanelMode>('empty');
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);

  useEffect(() => {
    if (locations.length > 0 && !selectedOfficeId) {
      setSelectedOfficeId(locations[0].id);
    }
  }, [locations, selectedOfficeId]);

  useEffect(() => {
    setHolidayPanelMode('empty');
    setSelectedHoliday(null);
    setSelectedDate(undefined);
    setEditingHoliday(null);
  }, [selectedOfficeId]);

  useEffect(() => {
    if (!selectedHoliday?.date) return;
    if (isoKeyYear(selectedHoliday.date) !== selectedYear) {
      setSelectedHoliday(null);
      setHolidayPanelMode('empty');
      setSelectedDate(undefined);
    }
  }, [selectedYear, selectedHoliday]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    for (let y = currentYear - 1; y <= currentYear + 2; y++) {
      years.add(y);
    }
    for (const h of holidays) {
      if (h.date) years.add(isoKeyYear(h.date));
    }
    return [...years].sort((a, b) => a - b).map((y) => ({ value: String(y), label: String(y) }));
  }, [holidays, currentYear]);

  const handleAddHoliday = () => {
    setEditingHoliday({
      id: '',
      name: '',
      date: '',
      type: 'national',
      isRecurring: false,
      description: '',
      officeId: selectedOfficeId,
      not_working_percentage: 1.0,
    });
    setHolidayPanelMode('add');
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await locationModelService.deleteLocationModel(id);
      queryClient.invalidateQueries({ queryKey: ['locationModels', selectedOfficeId] });
      setSelectedHoliday(null);
      setSelectedDate(undefined);
      setHolidayPanelMode('empty');
    } catch (error) {
      toast.error(
        `Failed to delete holiday: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const handleDateClick = useCallback(
    (date: Date, holiday: Holiday | null) => {
      setSelectedDate(date);
      if (holiday) {
        setSelectedHoliday(holiday);
        setHolidayPanelMode('selected');
        setEditingHoliday(null);
        return;
      }
      setEditingHoliday({
        id: '',
        name: '',
        date: dateToIsoKey(date),
        type: 'national',
        isRecurring: false,
        description: '',
        officeId: selectedOfficeId,
        not_working_percentage: 1.0,
      });
      setSelectedHoliday(null);
      setHolidayPanelMode('add');
    },
    [selectedOfficeId]
  );

  const handleHolidayFormClose = () => {
    if (holidayPanelMode === 'edit') {
      setHolidayPanelMode('selected');
    } else {
      setHolidayPanelMode('empty');
      setSelectedDate(undefined);
    }
    setEditingHoliday(null);
  };

  const handleHolidayViewClose = () => {
    setHolidayPanelMode('empty');
    setSelectedHoliday(null);
    setSelectedDate(undefined);
    setEditingHoliday(null);
  };

  const officeOptions = locations.map((l) => ({ value: l.id, label: l.name }));
  const holidayDetailOpen = holidayPanelMode !== 'empty';
  const loadFailed = locationModelsError || (locationModelsData && !locationModelsData.success);

  return (
    <div className="p-2">
      <SettingsListDetailLayout
        detailOpen={holidayDetailOpen}
        detail={
          holidayPanelMode === 'edit' || holidayPanelMode === 'add' ? (
            editingHoliday ? (
              <HolidayDrawer
                isOpen={true}
                onClose={handleHolidayFormClose}
                holiday={editingHoliday}
                selectedOfficeId={selectedOfficeId}
                existingHolidays={holidays}
                onSaved={() => {
                  queryClient.invalidateQueries({
                    queryKey: ['locationModels', selectedOfficeId],
                  });
                  setHolidayPanelMode('empty');
                  setSelectedHoliday(null);
                  setSelectedDate(undefined);
                  setEditingHoliday(null);
                }}
              />
            ) : null
          ) : holidayPanelMode === 'selected' && selectedHoliday ? (
            <HolidayDrawer
              key={selectedHoliday.id}
              isOpen={true}
              readOnly
              holiday={selectedHoliday}
              selectedOfficeId={selectedOfficeId}
              existingHolidays={holidays}
              onClose={handleHolidayViewClose}
              onEditRequest={() => {
                setEditingHoliday(selectedHoliday);
                setHolidayPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDeleteHoliday(selectedHoliday.id)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-2 flex shrink-0 items-center justify-between">
              <div>
                <div className="flex items-center gap-4">
                  <SelectFormField
                    label="Office name"
                    value={selectedOfficeId}
                    onChange={setSelectedOfficeId}
                    options={officeOptions}
                    className="max-w-xs min-w-[150px]"
                  />
                  <SelectFormField
                    label="Year"
                    value={String(selectedYear)}
                    onChange={(v) => setSelectedYear(Number.parseInt(v, 10))}
                    options={yearOptions}
                    className="max-w-xs min-w-[100px]"
                  />
                </div>
              </div>
              <Button type="button" onClick={handleAddHoliday}>
                <PlusIcon data-icon="inline-start" />
                Add Holiday
              </Button>
            </div>

            {isLoadingLocationModels ? (
              <p className="p-8 text-center text-text-secondary">Loading holidays...</p>
            ) : loadFailed ? (
              <p className="p-8 text-center text-danger-600">
                {locationModelsError
                  ? 'Failed to load holidays. Please try again.'
                  : locationModelsData?.message || 'Failed to load holidays.'}
              </p>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto pb-4">
                <HolidayYearCalendar
                  year={selectedYear}
                  holidays={holidays}
                  selectedDate={selectedDate}
                  onDateClick={handleDateClick}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
